import { Note } from '@/@types/note';
import { NOTE_TYPE_LABELS, NOTE_TYPES } from '@/modules/notes/constants';
import {
  NoteFormData,
  noteFormSchema,
} from '@/modules/notes/schemas/note-form-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

type NoteFormProps = {
  note?: Note;
  isLoading: boolean;
  // eslint-disable-next-line no-unused-vars
  onSubmit: (data: NoteFormData) => void;
  onCancel: () => void;
};

export function NoteForm({
  note,
  isLoading,
  onSubmit,
  onCancel,
}: NoteFormProps) {
  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<NoteFormData>({
    resolver: zodResolver(noteFormSchema),
    defaultValues: {
      title: note?.title || '',
      content: note?.content || '',
      type: note?.type || NOTE_TYPES[0],
    },
  });

  useEffect(() => {
    reset({
      title: note?.title || '',
      content: note?.content || '',
      type: note?.type || NOTE_TYPES[0],
    });
  }, [note, reset]);

  return (
    <>
      <DialogContent dividers sx={{ py: 3, px: 2 }}>
        <Box component="form">
          <Stack spacing={3}>
            <TextField
              autoFocus
              label="Título"
              error={!!errors.title}
              helperText={errors.title?.message}
              disabled={isLoading}
              {...register('title')}
            />
            <FormControl fullWidth error={!!errors.type} disabled={isLoading}>
              <InputLabel id="note-type-label">Tipo</InputLabel>
              <Controller
                name="type"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    labelId="note-type-label"
                    label="Tipo"
                    value={field.value || NOTE_TYPES[0]}
                  >
                    {NOTE_TYPES.map((type) => (
                      <MenuItem key={type} value={type}>
                        {NOTE_TYPE_LABELS[type]}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              <FormHelperText>{errors.type?.message}</FormHelperText>
            </FormControl>
            <TextField
              label="Conteúdo"
              multiline
              minRows={5}
              error={!!errors.content}
              helperText={errors.content?.message}
              disabled={isLoading}
              {...register('content')}
            />
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button disabled={isLoading} onClick={onCancel}>
          Cancelar
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={isLoading}
          onClick={handleSubmit(onSubmit)}
        >
          Salvar
        </Button>
      </DialogActions>
    </>
  );
}
